import { useTranslation } from 'react-i18next';
import { Layer, Source } from 'react-map-gl';

import { HABITAT_INTACTNESS_KEY, ThreatType } from '@/api/rest/resources/types/fact';
import { COMBINED_LAYER_FILL_LAYER_ID, COMBINED_LAYER_SOURCE_ID } from '@/pages/shared/plots/pages/constants';
import {
  InteractiveMapFormFields,
  useInteractiveMapControlsContext,
} from '@/pages/shared/plots/pages/hooks/useControlsForm';
import { useLayerUrl } from '@/pages/shared/plots/pages/hooks/useLayerUrl';
import { useMoveInteractiveLayersToTop } from '@/pages/shared/projects/pages/project/pages/conservation/shared/pages/interactive-map/hooks/useMoveInteractiveLayersToTop';
import { getMapBinaryColorForThreat } from '@/utils/getColorForThreat';

import { BinaryLegend, IndexLegend } from './Legend';

export const HabitatIntactnessLayers = () => {
  const controlsForm = useInteractiveMapControlsContext();

  const threatFilters = controlsForm.watch(InteractiveMapFormFields.ThreatFilters);

  const showHabitatIntactness = !threatFilters || threatFilters?.length === 0;

  return showHabitatIntactness ? <HabitatIntactnessLayer /> : <ThreatLayers />;
};

const HabitatIntactnessLayer = () => {
  const { t } = useTranslation();

  const controlsForm = useInteractiveMapControlsContext();

  const layerUrl = useLayerUrl(HABITAT_INTACTNESS_KEY);

  const habitatIntactnessMapColors = getMapBinaryColorForThreat(HABITAT_INTACTNESS_KEY);

  const legendColors = {
    from: habitatIntactnessMapColors.absence,
    to: habitatIntactnessMapColors.presence,
  };

  const [layer, year] = controlsForm.watch([InteractiveMapFormFields.Year, InteractiveMapFormFields.IndicatorLayer]);

  useMoveInteractiveLayersToTop(COMBINED_LAYER_SOURCE_ID);

  if (!layerUrl || !layer) {
    return null;
  }

  const RasterLayer = (
    <Source
      key={`${layer}-${year}`} // Force re-render when layer or year changes
      id={COMBINED_LAYER_SOURCE_ID}
      type='raster'
      tileSize={256}
      tiles={[layerUrl]}
    >
      <Layer
        id={COMBINED_LAYER_FILL_LAYER_ID}
        type='raster'
        paint={{
          'raster-opacity': 0.8,
          'raster-saturation': 0,
          'raster-contrast': 0.1,
        }}
      />
    </Source>
  );

  return (
    <>
      {RasterLayer}
      <IndexLegend
        // eslint-disable-next-line sonarjs/no-duplicate-string
        title={t('shared.projects.project.conservation.interactiveMap.labels.habitatIntactness')}
        infoPopoverProps={{
          title: t('shared.projects.project.conservation.interactiveMap.labels.habitatIntactness'),
          body: t('shared.projects.project.conservation.interactiveMap.explainers.habitatIntactness'),
        }}
        range={[
          {
            value: 0,
            color: legendColors.from,
          },
          {
            value: 100,
            color: legendColors.to,
          },
        ]}
      />
    </>
  );
};

const ThreatLayers = () => {
  const { t } = useTranslation();

  const controlsForm = useInteractiveMapControlsContext();

  const threatFilters = controlsForm.watch(InteractiveMapFormFields.ThreatFilters);

  if (!threatFilters || threatFilters.length === 0) {
    return null;
  }

  const threatLayerTooltips: Partial<Record<ThreatType, string>> = {
    [ThreatType.roads]: t('shared.projects.project.conservation.interactiveMap.infoPanel.tooltip.roads.description'),
  };

  return (
    <>
      {threatFilters.map((threat) => (
        <ThreatLayer key={threat} threat={threat} />
      ))}

      <BinaryLegend
        title={t('shared.projects.project.conservation.interactiveMap.labels.habitatIntactness')}
        layers={threatFilters.map((threat) => ({
          label: t(`global.threatTypes.${threat}`),
          color: getMapBinaryColorForThreat(threat).presence,
          tooltip: threatLayerTooltips[`${threat}`] ?? undefined,
        }))}
        infoPopoverProps={{
          title: t('shared.projects.project.conservation.interactiveMap.labels.habitatIntactness'),
          body: t('shared.projects.project.conservation.interactiveMap.explainers.habitatIntactness'),
        }}
      />
    </>
  );
};

const ThreatLayer = ({ threat }: { threat: ThreatType }) => {
  const layerUrl = useLayerUrl(threat);

  const controlsForm = useInteractiveMapControlsContext();

  const [layer, year] = controlsForm.watch([InteractiveMapFormFields.Year, InteractiveMapFormFields.IndicatorLayer]);

  useMoveInteractiveLayersToTop(threat);

  if (!layerUrl) {
    return null;
  }

  return (
    <Source
      key={`${layer}-${year}`} // Force re-render when layer or year changes
      id={threat}
      type='raster'
      tileSize={256}
      tiles={[layerUrl]}
    >
      <Layer
        id={`feat-${threat}-fill`}
        type='raster'
        paint={{
          'raster-opacity': 1,
        }}
      />
    </Source>
  );
};
